/*
 * AI quota helpers — formatting, warning thresholds, upgrade targets.
 *
 * The numeric source of truth lives in the backend (`GET /api/seo/quota`).
 * This module ONLY transforms what the backend returned. Do not duplicate
 * limit logic here.
 */

// Warning level for the usage bar / badges. Returns one of
// 'ok' | 'warn80' | 'warn90' | 'reached'.
export function quotaWarningLevel(used, total) {
  if (total === -1) return 'ok';                 // unlimited
  if (total <= 0) return 'reached';              // plan has no AI access
  const pct = (used / total) * 100;
  if (used >= total) return 'reached';
  if (pct >= 90) return 'warn90';
  if (pct >= 80) return 'warn80';
  return 'ok';
}

// Human label for the warning badge.
export function quotaWarningLabel(level, language = 'fr') {
  const map = {
    fr: {
      ok: '',
      warn80: '⚠️ 80% du quota utilisé',
      warn90: '⚠️ 90% du quota utilisé',
      reached: '⛔ Quota atteint',
    },
    en: {
      ok: '',
      warn80: '⚠️ 80% of quota used',
      warn90: '⚠️ 90% of quota used',
      reached: '⛔ Quota reached',
    },
  };
  return (map[language] || map.fr)[level] || '';
}

// "2026-05" → "mai 2026" (fr) or "May 2026" (en).
export function formatPeriod(periodYm, language = 'fr') {
  if (!periodYm || typeof periodYm !== 'string') return '—';
  const m = /^(\d{4})-(\d{2})$/.exec(periodYm);
  if (!m) return periodYm;
  const year = Number(m[1]);
  const monthIdx = Number(m[2]) - 1;
  if (monthIdx < 0 || monthIdx > 11) return periodYm;
  const formatter = new Intl.DateTimeFormat(language === 'fr' ? 'fr-CA' : 'en-CA', {
    month: 'long', year: 'numeric',
  });
  return formatter.format(new Date(year, monthIdx, 1));
}

// Returns the next plan to upgrade to (or null if already top tier / unlimited).
export function upgradeTarget(plan) {
  const norm = String(plan || '').toLowerCase();
  if (norm === 'starter') return 'pro';
  if (norm === 'pro') return 'enterprise';
  return null;
}

export function upgradeCtaLabel(plan, language = 'fr') {
  const target = upgradeTarget(plan);
  if (!target) return '';
  const labels = {
    fr: { pro: 'Passer au forfait Pro', enterprise: 'Passer au forfait Enterprise' },
    en: { pro: 'Upgrade to Pro', enterprise: 'Upgrade to Enterprise' },
  };
  return (labels[language] || labels.fr)[target] || '';
}
